/* eslint-disable complexity */
import { formatMinorUnitsAmount, FormattedMessage, WrappedComponentProps, injectIntl } from '@futrli/localisation';
import { PREDICT_PRODUCT_SLUG, ProductSlug } from '@futrli/shared-services';
import { capitalize, invoke } from 'lodash';
import React, { ReactNode } from 'react';
import Button from 'carbon-react/lib/components/button';

import { PaymentFormProps } from '../../billing-forms';
import { PaymentForm } from '../../billing-forms/components/payment-form/payment-form';
import { paymentDetailsFormConfig } from '../../billing-forms/config/payment-details-form-config';
import { PaymentFormData, PaymentFormInfo } from '../../billing-forms/types';
import { UIElements } from '../../core/components/stripe-styled-components';
import { IntentActions } from '../../core/types';
import { hasExistingCharges as hasExistingChargesFn } from '../../utils/utils';

import styles from './payment-details.scss';
import { Banner } from './banner';
import { PaymentCompleteDialog } from './payment-complete-dialog';
import { ActivationEvent } from '../../billing-in-app/components/activate-product-dialog/activate-product-sidebar';

interface State {
  formData: PaymentFormData | undefined;
  isValidForm: boolean;
}

interface OwnProps {
  isLoading?: boolean;
  isSuccessPayment?: boolean;
  paymentInfo: PaymentFormInfo | null;
  onSuccessClick: () => Promise<void>;
  onSubmit: PaymentFormProps['onSubmit'];
  onStripeError?: PaymentFormProps['onStripeError'];
  onPreSubmit?: (formData?: PaymentFormData) => Promise<IntentActions | undefined>;
  setIsSubmitting: (isSubmitting: boolean) => void;
  error?: JSX.Element | null;
  children?: ReactNode;
}

type Props = OwnProps & WrappedComponentProps;

export class PaymentDetails extends React.PureComponent<Props, State> {
  public state: Readonly<State> = {
    formData: undefined,
    isValidForm: false
  };

  public render(): JSX.Element {
    const { error, paymentInfo, intl, isLoading, isSuccessPayment, onStripeError, setIsSubmitting } = this.props;
    const { formData, isValidForm } = this.state;
    const hasExistingCharges = hasExistingChargesFn(paymentInfo);
    const productSlug: ProductSlug = paymentInfo?.productSlug || PREDICT_PRODUCT_SLUG;

    return (
      <div className={styles.pageContainer}>
        <div className={styles.leftContainer}>
          <div>
            <h1 className={styles.pageTitle}>
              <FormattedMessage id="billing.payment_details" defaultMessage="Payment details" />
            </h1>

            {isSuccessPayment && this.renderSuccessPayment()}
            {!isSuccessPayment && (
              <React.Fragment>
                {paymentInfo && (
                  <div className={styles.basicFont} style={{ marginBottom: 32, fontWeight: 400 }}>
                    <div>
                      <FormattedMessage
                        id="billing.payment_details.payment_info"
                        defaultMessage={`
                          Looks like you've already been assigned a <strong>Futrli {futrliPlan} plan</strong>
                          with <strong>{integrations} {addOn} License(s)</strong> added to it.
                        `}
                        values={{
                          futrliPlan: paymentInfo.subscription.term,
                          integrations: paymentInfo.subscription.addons,
                          addOn: capitalize(paymentInfo.productSlug as string),
                          strong: (msg: string) => <strong>{msg}</strong>
                        }}
                      />
                    </div>
                    <div>
                      {hasExistingCharges ? (
                        <FormattedMessage
                          id="billing.payment_details.payment_quantity"
                          defaultMessage={`
                              We'll now collect a payment of <strong>{paymentAmount} (excluding tax)</strong>.
                            `}
                          values={{
                            paymentAmount: formatMinorUnitsAmount(
                              paymentInfo.totalCharges,
                              paymentInfo.subscription.currencyCode,
                              intl
                            ),
                            strong: (msg: string) => <strong>{msg}</strong>
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="billing.payment_details.storing_payment_details_for_future_upgrades"
                          defaultMessage={`
                              You are not going to be charged now. Payment details will be stored for future upgrades.
                            `}
                        />
                      )}
                    </div>
                  </div>
                )}
                <UIElements>
                  <PaymentForm
                    setIsSubmitting={setIsSubmitting}
                    externalError={error}
                    onSubmit={this.handleOnSubmit}
                    onStripeError={onStripeError}
                    onPreSubmit={this.handleOnPreSubmit}
                    usePaymentIntent={hasExistingCharges}
                    isSubmitting={isLoading}
                    onValidate={this.isValidForm}
                    formConfig={paymentDetailsFormConfig}
                    product={productSlug}
                    formData={formData}
                    formActions={
                      <Button
                        type="submit"
                        buttonType="primary"
                        disabled={!isValidForm || isLoading}
                        fullWidth
                        mt="24px"
                      >
                        <FormattedMessage id="common.lets_go" defaultMessage="Let's go!" />
                      </Button>
                    }
                  />
                </UIElements>
              </React.Fragment>
            )}
          </div>
        </div>
        <Banner />
      </div>
    );
  }

  private renderSuccessPayment = (): JSX.Element => {
    const { paymentInfo, intl } = this.props;

    return (
      <PaymentCompleteDialog
        onSubmit={this.handleOnClick}
        titleMessage={
          hasExistingChargesFn(paymentInfo)
            ? intl.formatMessage({ id: 'billing.payment_successful', defaultMessage: 'Payment was successful!' })
            : intl.formatMessage({ id: 'billing.all_good_to_go', defaultMessage: 'All good to go!' })
        }
      />
    );
  };

  private handleOnSubmit = (formData: PaymentFormData): void => {
    this.setState({ formData }, () => {
      this.props.onSubmit?.(formData);
    });
  };

  private handleOnPreSubmit = (formData: PaymentFormData) => {
    return invoke(this.props, 'onPreSubmit', formData);
  };

  private isValidForm = (isValidForm: boolean) => {
    this.setState({ isValidForm });
  };

  private handleOnClick = (event: ActivationEvent) => {
    event.stopPropagation();
    this.props.onSuccessClick();
  };
}

export const PaymentDetailsIntl = injectIntl(PaymentDetails);

export const PaymentDetailsWR: React.FC<OwnProps> = ({ children, ...props }) => {
  return <PaymentDetailsIntl {...props}>{children}</PaymentDetailsIntl>;
};
